import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Calendar, ArrowRight, ArrowLeft, Loader2, Lock, Tag, Crown } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { usePageMeta } from '../lib/seo';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import LockedDialog, { hasAccess } from '../components/LockedDialog';
import { useCategories } from '../hooks/useCategories';
import { ytId } from '../components/course-editor/content/meta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

const VideoDetail = () => {
  const { id } = useParams();
  const { lang, isRTL, t } = useLang();
  const { user } = useAuth();
  const { getLabel: getCategoryLabel } = useCategories();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  usePageMeta(video && (lang === 'ar' ? video.titleAr : (video.titleEn || video.titleAr)), video && (lang === 'ar' ? video.descAr : (video.descEn || video.descAr)));
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const Arrow = isRTL ? ArrowRight : ArrowLeft;
  const canAccess = hasAccess(user) || !!video?.is_free;

  useEffect(() => {
    setShowLock(!!video && !canAccess);
  }, [canAccess, video]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/videos');
        const found = (data || []).find(v => v.id === id);
        if (found && !cancelled) setVideo(found);
        else if (!cancelled) setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="pt-28 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </main>
    );
  }

  if (notFound) return <Navigate to="/library" replace />;

  return (
    <>
      <main className="page-y min-h-screen" data-testid="video-detail-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 text-sm"
            data-testid="back-to-library"
          >
            <Arrow className="w-4 h-4" /> {tx(lang, 'العودة إلى المكتبة', 'Back to library')}
          </button>

          {/* Locked banner for non-subscribers */}
          {!canAccess && (
            <div className="mb-6 rounded-2xl bg-amber-500/10 border-2 border-amber-500/40 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-slate-900" />
              </div>
              <div className="flex-1">
                <div className="text-amber-700 font-bold mb-0.5">{tx(lang, 'محتوى حصري للمشتركين', 'Subscribers only content')}</div>
                <div className="text-slate-600 text-sm">{tx(lang, 'اشترك لمشاهدة الفيديو كاملاً', 'Subscribe to watch the full video')}</div>
              </div>
              <Link to="/subscription" data-testid="video-banner-subscribe" className="btn-subscribe h-10 px-4 text-sm">
                <Crown className="w-4 h-4" />{tx(lang, 'اشترك الآن', 'Subscribe')}
              </Link>
            </div>
          )}

          {/* Video player */}
          <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-video bg-black mb-6 relative">
            {canAccess && ytId(video.youtube || '') ? (
              <iframe
                data-testid="video-youtube-player"
                src={`https://www.youtube-nocookie.com/embed/${ytId(video.youtube)}?rel=0&modestbranding=1`}
                title={video.titleAr}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            ) : canAccess ? (
              <video
                src={resolveUrl(video.url)}
                controls
                autoPlay
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                onContextMenu={(e) => e.preventDefault()}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full relative">
                <video
                  src={resolveUrl(video.url)}
                  className="w-full h-full object-cover pointer-events-none"
                  preload="metadata"
                  muted
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                  <div className="relative mb-3">
                    <div className="absolute inset-0 bg-amber-400/40 blur-2xl rounded-full" />
                    <div className="relative w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center shadow-2xl">
                      <Lock className="w-9 h-9 text-slate-900" strokeWidth={2.5} />
                    </div>
                  </div>
                  <Link
                    to="/subscription"
                    data-testid="video-player-subscribe"
                    className="mt-2 btn-subscribe h-11 px-5"
                  >
                    <Crown className="w-4 h-4" />{tx(lang, 'اشترك لمشاهدة الفيديو', 'Subscribe to watch')}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Title & metadata */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3 leading-tight">
              {lang === 'ar' ? video.titleAr : video.titleEn}
            </h1>
            <div className={`flex items-center gap-4 flex-wrap text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span dir="ltr">{(video.created_at || '').slice(0, 10)}</span>
              </div>
              {video.category && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold">
                  <Tag className="w-3 h-3" /> {getCategoryLabel(video.category, lang)}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {(video.descAr || video.descEn) && (
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <h2 className="text-slate-900 font-bold text-lg mb-3">{tx(lang, 'الوصف', 'Description')}</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {lang === 'ar' ? video.descAr : video.descEn}
              </p>
            </div>
          )}
        </div>
      </main>

      <LockedDialog open={showLock} onClose={() => setShowLock(false)} />
    </>
  );
};

export default VideoDetail;
